import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe } from 'src/app/shared/pipes/translate.pipe';
import { formatPercentage, passGuideText } from '../mock-state';
import { MockAttempt, MockResult, MockReviewOption, StatementAnswer } from '../mock.models';
import { MockService } from '../mock.service';
import { isSubscriptionRequired } from '../mock-subscription';

@Component({
  selector: 'app-mock-result',
  templateUrl: './mock-result.component.html',
  styleUrls: [
    '../../capitals/question-detail/question-detail.component.scss',
    './mock-result.component.scss'
  ]
})
export class MockResultComponent implements OnInit, OnDestroy {
  attempt: MockAttempt | null = null;
  result: MockResult | null = null;
  isSpinner = true;
  loadError = false;
  subscriptionRequired = false;
  readonly letters = ['A', 'B', 'C', 'D', 'E'];

  private destroyed = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private mockService: MockService,
    private translate: TranslatePipe
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroyed)).subscribe(params => {
      const id = params.get('attemptId');
      if (id) {
        this.load(id);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  load(id: string): void {
    this.isSpinner = true;
    this.loadError = false;
    this.mockService.getAttempt(id).pipe(takeUntil(this.destroyed)).subscribe(attempt => {
      this.attempt = attempt;
      this.isSpinner = false;
      if (!attempt.result) {
        this.router.navigate(['/mock', id]);
        return;
      }
      this.result = attempt.result;
    }, error => {
      this.isSpinner = false;
      if (isSubscriptionRequired(error)) {
        this.subscriptionRequired = true;
        this.loadError = false;
        return;
      }
      this.loadError = true;
    });
  }

  percentText(value: number): string {
    return formatPercentage(value, localStorage.getItem('language') || 'de');
  }

  guideText(): string {
    if (!this.result) {
      return '';
    }
    return passGuideText(this.translate.transform('mockPassGuide'), this.result.pass_threshold);
  }

  letter(index: number): string {
    return this.letters[index - 1] || String(index);
  }

  answerLabel(value: StatementAnswer): string {
    if (value === true) {
      return this.translate.transform('mockTrue');
    }
    if (value === false) {
      return this.translate.transform('mockFalse');
    }
    return this.translate.transform('mockBlankAnswer');
  }

  pointsLabel(points: number): string {
    const key = points === 1 ? 'mockPoint' : 'mockPoints';
    return points + ' ' + this.translate.transform(key);
  }

  /**
   * Symbol on the letter circle so a judged statement is not marked by colour alone:
   * check mark = point earned, cross = no point, dash = left blank. The words
   * ("Your answer ... Correct answer ...") are already in the review-meta line.
   */
  resultIcon(option: MockReviewOption): string {
    if (option.user_answer !== true && option.user_answer !== false) {
      return '\u2013';
    }
    return option.points === 1 ? '\u2713' : '\u2717';
  }

  circleClass(option: MockReviewOption): string {
    if (option.user_answer !== true && option.user_answer !== false) {
      return 'option-letter-circle option-blank';
    }
    return option.points === 1
      ? 'option-letter-circle option-correct'
      : 'option-letter-circle option-wrong';
  }

  rowClass(option: MockReviewOption): string {
    if (option.user_answer !== true && option.user_answer !== false) {
      return 'answer';
    }
    return option.points === 1
      ? 'answer check-question-text-correct'
      : 'answer check-question-text-wrong';
  }

  explanationClass(option: MockReviewOption): string {
    if (option.user_answer !== true && option.user_answer !== false) {
      return 'explanation';
    }
    return option.points === 1
      ? 'explanation option-correct-explanation'
      : 'explanation option-wrong-explanation';
  }
}
